/*
 * A run of text with its invisible characters drawn (invisible.ts). Each one becomes a chip that
 * shows its code point — "U+202E" — and carries its Unicode name as the tooltip, while the character
 * itself stays inside the chip: the code point is painted by the stylesheet (`::before`), so a
 * selection copies the original text and never the label, and the chip isolates its bidi context, so
 * an override inside it reverses nothing around it.
 *
 * A text without such a character is returned as the string it is, which is the common case and
 * costs one test of a regular expression.
 */

import { Fragment, useContext, type ReactNode } from "react"
import { RevealInvisible } from "./context.ts"
import { splitInvisible } from "./invisible.ts"

/**
 * Characters the author wrote, inside a body: with their invisible characters drawn where the body
 * reveals them (`MarkdownBody`'s `revealInvisible`), and as themselves everywhere else — the same DOM
 * a body that never heard of the reveal draws.
 */
export function Literal({ text }: { text: string }): ReactNode {
	return useContext(RevealInvisible) ? <InvisibleText text={text} /> : text
}

export function InvisibleText({ text }: { text: string }): ReactNode {
	const parts = splitInvisible(text)
	if (parts.length <= 1 && typeof parts[0] !== "object") return text
	return parts.map((part, index) =>
		typeof part === "string" ? (
			<Fragment key={index}>{part}</Fragment>
		) : (
			<span key={index} className="tb-invisible" data-code={part.code} title={part.name}>
				{part.char}
			</span>
		),
	)
}
